import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { btnOnLight } from '../utils/buttonStyles';

const imageDef: SlotGroupDefinition = {
  label: 'Feature Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Modern apartment interior',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Floor Plans CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Label', type: 'text', defaultValue: 'EXPLORE FLOOR PLANS' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const LIST_CLS =
  'font-body text-base leading-relaxed text-[var(--vibe-color-text-muted)] md:text-lg' +
  ' [&_ul]:list-disc [&_ul]:pl-6 [&_li]:mb-2 [&_p]:mb-4 [&_a]:text-primary [&_a]:underline';

export function ModernApartmentFeatures() {
  const image = useSlotGroup('modernapartmentfeatures.image', imageDef);
  const ctaLink = useSlotGroup('modernapartmentfeatures.cta_link', ctaLinkDef);

  return (
    <section className="bg-[var(--vibe-color-background)] py-16 md:py-24">
      <div className="mx-auto grid max-w-7xl items-center gap-10 px-6 sm:px-8 lg:grid-cols-2 lg:gap-16 xl:px-10">
        <EditableSlotGroup
          groupPath="modernapartmentfeatures.image"
          definition={imageDef}
          className="aspect-[4/3] w-full overflow-hidden lg:order-last"
        >
          {image.image ? (
            <VibesitesImage
              url={image.image}
              alt={image.alt || 'Modern apartment interior'}
              sizes="(min-width: 1024px) 50vw, 100vw"
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="h-full w-full bg-muted" />
          )}
        </EditableSlotGroup>

        <div className="min-w-0">
          <RichTextContent
            path="modernapartmentfeatures.heading"
            as="h2"
            className="font-heading mb-6 text-2xl font-bold uppercase tracking-[0.1em] text-[var(--vibe-color-text)] md:text-3xl"
          />
          <RichTextContent
            path="modernapartmentfeatures.features"
            as="div"
            className={LIST_CLS}
          />
          <EditableSlotGroup
            groupPath="modernapartmentfeatures.cta_link"
            definition={ctaLinkDef}
          >
            <a
              href={ctaLink.url || '/floor-plans'}
              target={ctaLink._target || '_self'}
              className={`mt-8 inline-flex items-center justify-center px-10 py-4 font-body text-sm font-medium uppercase tracking-[0.2em] ${btnOnLight}`}
            >
              {ctaLink.label || 'EXPLORE FLOOR PLANS'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ModernApartmentFeatures',
  displayName: 'Modern Apartment Features',
  tokens: {
    'modernapartmentfeatures.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Apartment Features</p>',
    },
    'modernapartmentfeatures.features': {
      label: 'Features List',
      type: 'richtext',
      defaultValue:
        '<ul>' +
        '<li>Spacious, open-concept floor plans designed for effortless flow and natural light</li>' +
        '<li>Sleek vinyl plank flooring throughout living areas</li>' +
        '<li>Quartz countertops and stainless-steel appliances in every kitchen</li>' +
        '<li>Kitchen islands perfect for cooking, entertaining, or casual dining</li>' +
        '<li>Full-sized washer and dryer conveniently located in each home</li>' +
        '<li>Central air conditioning for year-round comfort</li>' +
        '<li>Generous walk-in closets offering plenty of storage space</li>' +
        '</ul>',
    },
  },
  slotGroups: {
    'modernapartmentfeatures.image': imageDef,
    'modernapartmentfeatures.cta_link': ctaLinkDef,
  },
};

(ModernApartmentFeatures as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default ModernApartmentFeatures;
